import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Gift,
  Link as LinkIcon,
  Users,
  DollarSign,
  TrendingUp,
  Copy,
  Check,
  Clock,
  AlertCircle,
  Loader2,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  affiliateService,
  Affiliate,
  AffiliateStats,
  Referral,
  Commission,
  BankInfo
} from '../../services/affiliateService';

const AffiliateSettings: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isSavingBank, setIsSavingBank] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'referrals' | 'commissions' | 'payout'>('overview');

  const [bankInfo, setBankInfo] = useState<BankInfo>({
    bank_name: '',
    account_type: '',
    account_number: '',
    pix_key: '',
    paypal_email: ''
  });

  // Use production URL for affiliate links
  const baseUrl = 'https://admin-marketing.devskin.com';

  useEffect(() => {
    loadAffiliateData();
  }, []);

  const loadAffiliateData = async () => {
    setIsLoading(true);
    try {
      const statusResponse = await affiliateService.getStatus();
      if (statusResponse.success && statusResponse.data) {
        setAffiliate(statusResponse.data);
        if (statusResponse.data.bank_info) {
          setBankInfo(statusResponse.data.bank_info);
        }

        if (statusResponse.data.status === 'approved') {
          const [statsRes, referralsRes, commissionsRes] = await Promise.all([
            affiliateService.getStats(),
            affiliateService.getReferrals({ limit: 10 }),
            affiliateService.getCommissions({ limit: 10 })
          ]);

          if (statsRes.success && statsRes.data) setStats(statsRes.data);
          if (referralsRes.success) setReferrals(referralsRes.data || []);
          if (commissionsRes.success) setCommissions(commissionsRes.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading affiliate data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const response = await affiliateService.apply();
      if (response.success && response.data) {
        setAffiliate(response.data);
        toast.success(t('affiliate.apply_success'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('affiliate.apply_error'));
    } finally {
      setIsApplying(false);
    }
  };

  const handleSaveBankInfo = async () => {
    setIsSavingBank(true);
    try {
      const response = await affiliateService.updateBankInfo(bankInfo);
      if (response.success) {
        toast.success(t('affiliate.bank_info_updated'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('affiliate.bank_info_error'));
    } finally {
      setIsSavingBank(false);
    }
  };

  const copyReferralLink = () => {
    if (!affiliate?.referral_code) return;
    const link = `${baseUrl}/signup?ref=${affiliate.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(t('affiliate.link_copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'suspended':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Not an affiliate - show apply section
  if (!affiliate) {
    return (
      <div className="space-y-3 sm:space-y-6">
        {/* Hero Section */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-primary-600 via-purple-600 to-pink-500 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Gift className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {t('affiliate.become_affiliate')}
                </h2>
                <p className="text-white/80">
                  {t('affiliate.become_affiliate_desc')}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {t('affiliate.benefit_commission_title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('affiliate.benefit_commission')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {t('affiliate.benefit_recurring_title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('affiliate.benefit_recurring')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <Card>
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('affiliate.ready_to_start')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('affiliate.ready_to_start_desc')}
              </p>
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={handleApply}
              isLoading={isApplying}
              leftIcon={<Gift className="w-5 h-5" />}
              className="whitespace-nowrap"
            >
              {t('affiliate.apply_now')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending approval
  if (affiliate.status === 'pending') {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {t('affiliate.pending_title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('affiliate.pending_desc')}
            </p>
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge('pending')}`}>
              {t('affiliate.status_pending')}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Rejected
  if (affiliate.status === 'rejected') {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {t('affiliate.rejected_title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('affiliate.rejected_desc')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Approved - Full dashboard
  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Referral Link Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5" />
            {t('affiliate.your_referral_link')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Input
              value={`${baseUrl}/signup?ref=${affiliate.referral_code}`}
              readOnly
              className="flex-1 font-mono text-sm"
            />
            <Button
              variant="primary"
              onClick={copyReferralLink}
              leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            >
              {copied ? t('common.copied') : t('common.copy')}
            </Button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {t('affiliate.referral_code')}: <span className="font-mono font-semibold">{affiliate.referral_code}</span>
          </p>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('affiliate.total_referrals')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_referrals}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {stats.converted_referrals} {t('affiliate.converted')} ({stats.conversion_rate}%)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('affiliate.total_earnings')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(Number(stats.total_earnings))}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {stats.commission_percentage}% {t('affiliate.commission_rate')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('affiliate.pending_earnings')}</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatCurrency(Number(stats.pending_earnings))}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {stats.pending_commissions} {t('affiliate.pending_commissions')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('affiliate.paid_earnings')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(Number(stats.paid_earnings))}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {stats.paid_commissions} {t('affiliate.paid_commissions')}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4 border-b border-gray-200 dark:border-gray-700 -mb-4 -mx-4 px-4">
            {(['overview', 'referrals', 'commissions', 'payout'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {t(`affiliate.tab_${tab}`)}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                {t('affiliate.overview_desc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {t('affiliate.how_it_works_title')}
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 flex items-center justify-center text-xs font-bold">1</span>
                      {t('affiliate.step_1')}
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 flex items-center justify-center text-xs font-bold">2</span>
                      {t('affiliate.step_2')}
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 flex items-center justify-center text-xs font-bold">3</span>
                      {t('affiliate.step_3')}
                    </li>
                  </ul>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                    {t('affiliate.your_commission')}
                  </h4>
                  <p className="text-3xl font-bold text-green-600">
                    {stats?.commission_percentage || 20}%
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    {t('affiliate.recurring_commission')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'referrals' && (
            <div>
              {referrals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('affiliate.no_referrals')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {referrals.map((referral) => (
                    <div
                      key={referral.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary-600">
                            {referral.referredUser?.first_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {referral.referredUser?.first_name} {referral.referredUser?.last_name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{referral.referredUser?.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          referral.status === 'converted'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {t(`affiliate.referral_${referral.status}`)}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(referral.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'commissions' && (
            <div>
              {commissions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('affiliate.no_commissions')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {commissions.map((commission) => (
                    <div
                      key={commission.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          commission.status === 'paid'
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : commission.status === 'approved'
                            ? 'bg-blue-100 dark:bg-blue-900/30'
                            : 'bg-yellow-100 dark:bg-yellow-900/30'
                        }`}>
                          {commission.status === 'paid' ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {commission.referral?.referredUser?.first_name} {commission.referral?.referredUser?.last_name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {commission.subscription?.plan?.name} - {commission.percentage}%
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(Number(commission.amount))}
                        </p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          commission.status === 'paid'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : commission.status === 'approved'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {t(`affiliate.commission_${commission.status}`)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'payout' && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('affiliate.payout_desc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('affiliate.pix_key')}
                  </label>
                  <Input
                    value={bankInfo.pix_key || ''}
                    onChange={(e) => setBankInfo({ ...bankInfo, pix_key: e.target.value })}
                    placeholder={t('affiliate.pix_key_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('affiliate.paypal_email')}
                  </label>
                  <Input
                    type="email"
                    value={bankInfo.paypal_email || ''}
                    onChange={(e) => setBankInfo({ ...bankInfo, paypal_email: e.target.value })}
                    placeholder={t('affiliate.paypal_email_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('affiliate.bank_name')}
                  </label>
                  <Input
                    value={bankInfo.bank_name || ''}
                    onChange={(e) => setBankInfo({ ...bankInfo, bank_name: e.target.value })}
                    placeholder={t('affiliate.bank_name_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('affiliate.account_number')}
                  </label>
                  <Input
                    value={bankInfo.account_number || ''}
                    onChange={(e) => setBankInfo({ ...bankInfo, account_number: e.target.value })}
                    placeholder={t('affiliate.account_number_placeholder')}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  variant="primary"
                  onClick={handleSaveBankInfo}
                  isLoading={isSavingBank}
                >
                  {t('common.save')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateSettings;
